import { TrashIcon } from "@heroicons/react/24/solid";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAlert } from "../../hooks";
import PrimaryButton from "../Buttons/PrimaryButton";
import { NumberInput, TextInput } from "../Inputs";
import PrimaryTable from "../Tables/PrimaryTable";
import ModalWrapper from "./ModalWrapper";

export default function EnumEntityModal({
  entity,
  entities,
  open,
  setOpen,
  handleAddEntity,
}) {
  const alert = useAlert();
  const [enumName, setEnumName] = useState("");
  const [enumValue, setEnumValue] = useState(0);
  const [enumDescription, setEnumDescription] = useState("");
  const [entityEdit, setEntityEdit] = useState({ ...entity });

  useEffect(() => {
    setEntityEdit({ ...entity });
  }, [entity]);

  useEffect(() => {
    clearValues();
  }, [open]);

  const handleEnumTableNameChange = (name) => {
    let entityEditTemp = { ...entityEdit, name };
    setEntityEdit(entityEditTemp);
  };

  const addEnumValue = () => {
    if (entityEdit.enum.values.find((x) => x.name === enumName)) {
      alert("Invalid input", "Item name already exists", "warning");
      return false;
    }

    if (!/^[a-zA-Z]+$/.test(enumName)) {
      alert(
        "Invalid input",
        "No spaces allowed for item name (only allows for alphabetic characters)",
        "warning"
      );
      return false;
    }

    if (entityEdit.enum.values.find((x) => x.value === enumValue)) {
      alert("Invalid input", "Item value already exists", "warning");
      return false;
    }

    if (entityEdit.enum.values.find((x) => x.description === enumDescription)) {
      alert("Invalid input", "Item description already exists", "warning");
      return false;
    }

    let values = [
      ...entityEdit.enum.values,
      {
        name: enumName,
        value: enumValue,
        description: enumDescription,
        property: "Name",
        id: uuidv4(),
      },
    ];
    let entityEditTemp = { ...entityEdit, enum: { values } };
    setEntityEdit(entityEditTemp);

    setEnumName("");
    setEnumValue((x) => x + 1);
    setEnumDescription("");
  };

  const clearValues = () => {
    setEnumName("");
    setEnumValue(0);
    setEnumDescription("");
  };

  const handleRemoveEnumValue = (id) => {
    let values = entityEdit.enum.values.filter((x) => x.id !== id);
    let entityEditTemp = { ...entityEdit, enum: { values } };
    setEntityEdit(entityEditTemp);
  };

  const handleAddEnum = () => {
    if (!entityEdit.name || !entityEdit.name.trim()) {
      alert("Invalid input", "Entity must have a name", "warning");
      return false;
    }
    
    if (
      entities
        .filter((x) => x.id !== entityEdit.id)
        .map((x) => x.name)
        .find((x) => x.toLowerCase() === entityEdit.name.toLowerCase())
    ) {
      alert("Invalid input", "Entity name already exists", "warning");
      return false;
    }

    if (!entityEdit.enum.values || entityEdit.enum.values.length === 0) {
      alert(
        "Invalid input",
        "An enum should have some records in it",
        "warning"
      );
      return false;
    }

    handleAddEntity(entityEdit);
    setOpen(false);
    clearValues();
  };

  return (
    <ModalWrapper open={open} setOpen={setOpen}>
      <div className="w-[85vw] lg:w-[35vw]">
        <div className="pb-8 mx-6 text-center text-sm text-gray-500">
          Enum information
        </div>

        <div className="mb-8">
          <TextInput
            label="Table Name"
            placeholder="Enter Table Name (singular, PascalCase)"
            value={entityEdit.name || ""}
            onChange={(e) => handleEnumTableNameChange(e.target.value)}
          />
        </div>

        <div className="mb-8">
          <TextInput
            label="Item Description"
            subLabel="Value in table and description attribute in enum (eg 'System Administrator')"
            placeholder="Enter Item Description"
            value={enumDescription}
            onChange={(e) => setEnumDescription(e.target.value)}
          />
        </div>

        <div className="mb-8">
          <TextInput
            label="Item Name"
            subLabel="Name of item in enum (eg 'SysAdmin') (this will be name in table if a description is not provided)"
            placeholder="Enter Item Name"
            value={enumName}
            onChange={(e) => setEnumName(e.target.value)}
          />
        </div>

        <div className="mb-8">
          <NumberInput
            label="Item Value"
            placeholder="Enter Item Value"
            value={enumValue}
            onChange={(e) => setEnumValue(e.target.valueAsNumber)}
          />
        </div>

        <div className="mt-4">
          <PrimaryButton
            text="Add enum value"
            onClick={addEnumValue}
            disabled={enumName.trim() === ""}
          />
        </div>
        <div className="mt-4">
          <div className="my-4 p-4 bg-gray-200 rounded-lg">
            <h3 className="font-bold text-xl mb-3">{entityEdit?.name}</h3>

            <PrimaryTable
              paginatorHidden
              header={
                <PrimaryTable.Header>
                  <PrimaryTable.HeaderColumn display="Name" />
                  <PrimaryTable.HeaderColumn display="Value" />
                  <PrimaryTable.HeaderColumn display="Description" />
                  <PrimaryTable.HeaderColumn />
                </PrimaryTable.Header>
              }
            >
              {_.map(entityEdit?.enum?.values, (x) => (
                <PrimaryTable.Row key={`${x.value}`}>
                  <PrimaryTable.Cell>{x.name}</PrimaryTable.Cell>
                  <PrimaryTable.Cell>{x.value}</PrimaryTable.Cell>
                  <PrimaryTable.Cell>{x.description}</PrimaryTable.Cell>
                  <PrimaryTable.Cell>
                    <TrashIcon
                      className="w-5 text-red-900 cursor-pointer hover:text-red-500"
                      onClick={() => handleRemoveEnumValue(x.id)}
                    />
                  </PrimaryTable.Cell>
                </PrimaryTable.Row>
              ))}
            </PrimaryTable>
          </div>
          <div className="py-2" />
          <PrimaryButton text="Add Entity to Project" onClick={handleAddEnum} />
        </div>
      </div>
    </ModalWrapper>
  );
}
